import { StyleSheet, Text, View } from "react-native";

import { scaled } from "../pictureScale";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import type { UiTheme } from "../theme";

// #389: two guides inside the plot box of `WagmiLineChart`, opt-in. A
// baseline (a hairline at one y, its value in mono at its left) and an end
// label (the headline curve's last value, at its end). Plain Views and Texts
// laid over the plot, not SVG text: nothing here draws SVG text yet, and the
// mono face in an SVG is not proven on native. Mounted under the tap layer,
// so a tap still reaches it. Decorative to a screen reader: the plot's own
// label and the head's readout say the same values.

/** Room a label takes, top to bottom (sm text). */
const labelHeight = (theme: UiTheme) => Math.ceil(theme.typography.sizes.sm * 1.35);

/** The hairline at `y` (px from the top), under the curves. */
export function ChartBaseline({
  theme,
  y,
  left,
  width,
}: {
  theme: UiTheme;
  y: number;
  left: number;
  width: number;
}) {
  const styles = makeStyles(theme);
  return (
    <View
      {...hiddenFromScreenReader()}
      pointerEvents="none"
      style={[styles.line, { top: y, left, width }]}
    />
  );
}

/** The hairline's label, on the side `labelBelow` asks for when it fits
 *  there, else the other. Mounted OVER the curves, on a surface backing, so
 *  a curve's first points never run through it. */
export function ChartBaselineLabel({
  theme,
  y,
  left,
  height,
  label,
  labelBelow,
}: {
  theme: UiTheme;
  y: number;
  left: number;
  height: number;
  label: string;
  labelBelow: boolean;
}) {
  const styles = makeStyles(theme);
  const lh = labelHeight(theme);
  const below = labelBelow ? y + lh <= height : y - lh < 0;
  return (
    <Text
      {...hiddenFromScreenReader()}
      pointerEvents="none"
      maxFontSizeMultiplier={1}
      style={[styles.label, { left, top: below ? y + 1 : y - lh }]}
    >
      {label}
    </Text>
  );
}

/** The last value of a curve, beside its last point (`x`, `y` in px from the
 *  plot's left and top). Above the point when the curve rose into it, below
 *  when it fell, so the label sits off the last segment; ends at the point,
 *  or starts there when the point is too far left to end on it. */
export function ChartEndLabel({
  theme,
  x,
  y,
  plotWidth,
  height,
  label,
  rose,
}: {
  theme: UiTheme;
  x: number;
  y: number;
  plotWidth: number;
  height: number;
  label: string;
  rose: boolean;
}) {
  const styles = makeStyles(theme);
  const lh = labelHeight(theme);
  // Past the last dot's radius (up to 7 in a picture), plus air.
  const gap = scaled(theme, 10);
  const above = rose ? y - gap - lh >= 0 : y + gap + lh > height;
  const top = above ? y - gap - lh : y + gap;
  const fromLeft = x < scaled(theme, 48);
  return (
    <Text
      {...hiddenFromScreenReader()}
      maxFontSizeMultiplier={1}
      numberOfLines={1}
      pointerEvents="none"
      style={[styles.end, fromLeft ? { left: x + gap, top } : { right: plotWidth - x, top }]}
    >
      {label}
    </Text>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: ty } = theme;
  const mono = { fontFamily: ty.fonts.mono, fontVariant: ["tabular-nums" as const], fontSize: ty.sizes.sm };
  const backing = { backgroundColor: p.surface, paddingHorizontal: scaled(theme, 2), borderRadius: scaled(theme, 2) };
  return StyleSheet.create({
    line: { position: "absolute", height: scaled(theme, 1), backgroundColor: p.ink3 },
    label: { ...mono, position: "absolute", color: p.ink3, lineHeight: labelHeight(theme), ...backing },
    end: { ...mono, position: "absolute", color: p.ink, fontWeight: "700", lineHeight: labelHeight(theme), ...backing },
  });
}
